import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { name: 'Home', path: '/homepage', icon: 'Home' },
    { name: 'Categories', path: '/category-pages', icon: 'Grid3X3' },
    { name: 'Cart', path: '/shopping-cart', icon: 'ShoppingCart' },
    { name: 'Dashboard', path: '/user-dashboard', icon: 'User' },
  ];

  const moreMenuItems = [
    { name: 'Checkout', path: '/checkout', icon: 'CreditCard' },
    { name: 'Admin', path: '/admin-dashboard', icon: 'Settings' },
  ];

  const isActivePath = (path) => location?.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-neural' 
          : 'bg-background/80 backdrop-blur-sm'
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6">
          {/* Logo Section */}
          <Link to="/homepage" className="flex items-center space-x-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center trust-seal">
                <Icon name="Zap" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold command-text bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                ElectroShop Pro
              </span>
              <span className="text-xs text-muted-foreground font-mono tracking-wider">
                TECH COMMAND CENTER
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`neural-nav-item px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-300 ${
                  isActivePath(item?.path)
                    ? 'bg-primary/20 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span className="font-medium">{item?.name}</span>
              </Link>
            ))}
            
            {/* More Menu */}
            <div className="relative group">
              <Button
                variant="ghost"
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="MoreHorizontal" size={18} />
                <span className="font-medium">More</span>
              </Button>
              
              <div className="absolute top-full right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  {moreMenuItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                        isActivePath(item?.path)
                          ? 'bg-primary/20 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Icon 
                name="Search" 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
              />
              <input
                type="text"
                placeholder="Search products..."
                className="w-64 pl-10 pr-4 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200"
              />
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-2">
              <div className="trust-seal p-2 bg-success/10 border border-success/30 rounded-lg">
                <Icon name="Shield" size={16} color="var(--color-success)" />
              </div>
              <div className="text-xs text-muted-foreground">
                <div className="font-mono">SSL Secured</div>
                <div className="flex items-center space-x-1">
                  <div className="inventory-pulse high"></div>
                  <span>Live Inventory</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-background/95 backdrop-blur-md border-t border-border">
            <div className="px-6 py-4 space-y-2">
              {/* Mobile Search */}
              <div className="relative mb-4">
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
                <input
                  type="text"
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              </div>

              {/* Mobile Navigation */}
              {[...navigationItems, ...moreMenuItems]?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary/20 text-primary border border-primary/30' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium">{item?.name}</span>
                </Link>
              ))}

              {/* Mobile Trust Indicators */}
              <div className="flex items-center justify-center space-x-4 pt-4 border-t border-border mt-4">
                <div className="flex items-center space-x-2">
                  <div className="trust-seal p-2 bg-success/10 border border-success/30 rounded-lg">
                    <Icon name="Shield" size={16} color="var(--color-success)" />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <div className="font-mono">SSL Secured</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="inventory-pulse high"></div>
                  <span className="text-xs text-muted-foreground">Live Inventory</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Data Stream Animation */}
      <div className="data-stream"></div>
    </header>
  );
};

export default Header;